import { Heading, LinkProps } from '@chakra-ui/react';
import { UserRoleEnum } from '@monetize/types/app';
import { FiExternalLink } from 'react-icons/fi';
import { Link as ReactDomLink } from 'react-router-dom';
import { useAuth } from '../../../services/auth0';
import { MBox, MIcon, MLink } from '../chakra';
import { MTooltip } from '../MTooltip';
import { MEntityStatusBadge } from '../MEntityStatusBadge';
import { MIDCopyBox } from '../MIDCopyBox';

interface RecordLookupPopoverHeaderProps {
  recordId: string;
  href: string;
  linkProps?: LinkProps;
  recordName: string | (() => string);
  displayStatus?: string;
  secondaryDisplayStatus?: string;
  /**
   * If provided, link will only be available to provided roles.
   * If not provided, link will be available to all users regardless of role
   */
  roles?: UserRoleEnum[];
  /**
   * When true, the record name renders as plain text with no link — e.g. where
   * the destination (Product Catalog edit page) isn't useful for the context.
   */
  hideEditLink?: boolean;
}

/**
 * Use for records that have a name
 * This will show the name as a link
 * Optional status badge (recommended)
 * and ability to copy a link
 */
export const RecordLookupPopoverHeader = ({
  recordId,
  href,
  linkProps,
  recordName,
  displayStatus,
  secondaryDisplayStatus,
  roles,
  hideEditLink = false,
}: RecordLookupPopoverHeaderProps) => {
  const { currentTenantUserHasRole } = useAuth();

  const showLink =
    !hideEditLink && (roles ? currentTenantUserHasRole(roles) : true);

  return (
    <>
      <Heading as="h3" size="sm" mb={1} display="flex">
        {showLink ? (
          <MBox as="span" alignItems="center" display="flex">
            <MLink asChild fontSize="md" {...linkProps}>
              <ReactDomLink to={href}>
                {typeof recordName === 'function' ? recordName() : recordName}
              </ReactDomLink>
            </MLink>
            <MTooltip label="Open in new tab" placement="top">
              <MLink
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                fontSize="md"
                alignItems="center"
                display="flex"
                {...linkProps}
              >
                <MIcon as={FiExternalLink} ml="1" color="tIndigo.base" />
              </MLink>
            </MTooltip>
          </MBox>
        ) : (
          <MBox as="span">
            {typeof recordName === 'function' ? recordName() : recordName}
          </MBox>
        )}
        {displayStatus && <MEntityStatusBadge status={displayStatus} />}
        {secondaryDisplayStatus && (
          <MEntityStatusBadge status={secondaryDisplayStatus} />
        )}
      </Heading>
      <MIDCopyBox copyValue={recordId} />
    </>
  );
};
